import { useState } from 'react';
import { Box, Divider, IconButton, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { RenderUnsettledUI, SmallError, SmallLoader } from 'shared/components';
import { FormProvider, useFormContext } from 'react-hook-form';
import {
  IconApproved,
  IconCall,
  IconCancel,
  IconCross,
  IconDocument,
  IconEdit,
  IconInbox,
} from 'assets/img';
import { LwButton, LwFormDate, LwFormSelect, LWModal, UserAvatar } from 'redesign';
import { DateTime } from 'luxon';
import {
  ButtonBigWrap,
  ButtonWrap,
  ButtonWrapCertificate,
  CertificateInfo,
  CertificateInfoWrapper,
  CertificateName,
  CertificateWrap,
  CredentialText,
  DateBox,
  FlexColumnBoxWrap,
  FlexRowBoxCenter,
  HeaderWrapper,
  ImageWrap,
  InfoWrap,
  ModalWrapperBox,
  NameBox,
  OpenCertificateButton,
  StyledIcon,
} from 'shared/components/common/certificate-modal-styled-components';
import {
  AccreditationDetailCommonProps,
  AccreditationDetailProps,
  ApproveAccreditationButtonProps,
  ButtonsBasedOnAccreditationStateProps,
  RejectAccreditationButtonProps,
  showsRejectionReasonField,
  UpdateAccreditationButtonProps,
} from './accreditations-detail.types';
import { useAccreditationDetailModalBody } from './hooks/use-accreditation-detail-modal-body';
import { useAccreditationDetailUpdateAction } from './hooks/use-accreditation-detail-update-action';
import { useAccreditationDetailApproveAction } from './hooks/use-accreditation-detail-approve-action';
import { useAccreditationDetailRejectAction } from './hooks/use-accreditation-detail-reject-action';
import { useGetAccreditationDetail } from '../hooks/use-get-accreditation';
import { FlexWorkerDetail } from '../../flexWorkers/flex-worker-detail/flex-worker-detail';
import { RawAccreditationData } from '../../../../services/accreditations-service.types';

export const AccreditationDetail = ({
  flexWorkerId,
  certificateId,
  accreditationType,
  close,
}: AccreditationDetailProps) => {
  const accreditationDetailQuery = useGetAccreditationDetail(
    certificateId,
    flexWorkerId,
    accreditationType
  );

  if (accreditationDetailQuery.status !== 'success') {
    return (
      <RenderUnsettledUI
        renderLoading={<SmallLoader />}
        renderError={(error) => <SmallError error={error} />}
        data={accreditationDetailQuery}
      />
    );
  }

  const { data } = accreditationDetailQuery;

  return (
    <ModalWrapperBox>
      <ProfileHeader data={data} close={close} />
      <ModalBody data={data} closeModal={close} />
    </ModalWrapperBox>
  );
};

const ProfileHeader = ({ data, close }: { data: RawAccreditationData; close: () => void }) => {
  const [open, setOpen] = useState(false);

  const toggleFwModal = () => {
    setOpen(!open);
  };

  return (
    <>
      <Box>
        <HeaderWrapper cursor="pointer">
          <FlexRowBoxCenter onClick={toggleFwModal} role="fw-profile">
            <ImageWrap>
              <UserAvatar
                firstName={data.flexWorker.firstName}
                lastName={data.flexWorker.lastName}
                imageUrl={data.flexWorker.imageUrl}
                width={14}
                height={14}
              />
            </ImageWrap>

            <Typography variant="h3" fontWeight={500} noWrap>
              {data?.flexWorker.firstName} {data?.flexWorker.lastName}
            </Typography>
          </FlexRowBoxCenter>

          <InfoWrap>
            <Box display="flex" flexDirection="row">
              <StyledIcon width={24} height={24} as={IconCall} />
              <CredentialText paddingRight={5} noWrap>
                {data?.flexWorker.phone}
              </CredentialText>
            </Box>
            <Box display="flex" flexDirection="row">
              <StyledIcon width={24} height={24} as={IconInbox} />
              <CredentialText noWrap>{data?.flexWorker.email}</CredentialText>
            </Box>
            <Box display="flex" flexDirection="row">
              <IconButton onClick={close}>
                <IconCross width={24} height={24} />
              </IconButton>
            </Box>
          </InfoWrap>
        </HeaderWrapper>
        <Divider />
      </Box>

      <LWModal isOpen={open} onClose={toggleFwModal} testId="detail-modal">
        {data.flexWorkerId ? (
          <FlexWorkerDetail id={data.flexWorkerId} onClose={toggleFwModal} />
        ) : null}
      </LWModal>
    </>
  );
};

const ModalBody = ({ data, closeModal }: AccreditationDetailCommonProps) => {
  const { t } = useTranslation('hq-screen-specific');
  const { methods, rejectionReasonOptions, rejectionReasonFieldRules, handleOpenAccreditation } =
    useAccreditationDetailModalBody(data);

  return (
    <FormProvider {...methods}>
      <FlexColumnBoxWrap>
        <CertificateInfo>
          <CertificateWrap>
            <FlexRowBoxCenter>
              <IconDocument width={130} height={150} onClick={handleOpenAccreditation} />
            </FlexRowBoxCenter>
            <ButtonWrapCertificate>
              <OpenCertificateButton
                onClick={handleOpenAccreditation}
                role="button"
                disabled={!data.fileUrl}
              >
                Open
              </OpenCertificateButton>
            </ButtonWrapCertificate>
          </CertificateWrap>
          <CertificateInfoWrapper>
            <NameBox>
              <CertificateName>{`Certificaat: ${data?.certificate.name}`}</CertificateName>
            </NameBox>
            <NameBox>
              <CertificateName>{`Certificaattype: ${data.type}`}</CertificateName>
            </NameBox>
            <DateBox>
              <LwFormDate
                name="expirationDate"
                label="Vervaldatum instellen"
                control={methods.control}
              />
            </DateBox>
            {showsRejectionReasonField(data.state) ? (
              <DateBox>
                <LwFormSelect
                  name="rejectionReason"
                  control={methods.control}
                  label={t('certificates.detail.rejection-reason.label')}
                  defaultLabel={t('certificates.detail.rejection-reason.placeholder')}
                  options={rejectionReasonOptions}
                  data-testid="accreditation-rejection-reason"
                  rules={rejectionReasonFieldRules}
                />
              </DateBox>
            ) : null}
          </CertificateInfoWrapper>
        </CertificateInfo>
        <Buttons data={data} closeModal={closeModal} />
      </FlexColumnBoxWrap>
    </FormProvider>
  );
};

const Buttons = ({ data, closeModal }: AccreditationDetailCommonProps) => {
  const methods = useFormContext();
  const expirationDate = methods.watch('expirationDate');
  const expirationAsDateTime = expirationDate ? DateTime.fromISO(expirationDate) : null;
  const startDate = null;
  const rejectionReason = methods.watch('rejectionReason');

  return (
    <ButtonBigWrap>
      <ButtonWrap>
        <ButtonsBasedOnAccreditationState
          accreditationState={data.state}
          flexWorkerId={data.flexWorkerId}
          accreditationType={data.type}
          certificateId={data.certificateId}
          expirationDate={expirationAsDateTime}
          startDate={startDate}
          rejectionReason={rejectionReason}
          closeModal={closeModal}
        />
      </ButtonWrap>
    </ButtonBigWrap>
  );
};

const ButtonsBasedOnAccreditationState = ({
  accreditationState,
  flexWorkerId,
  certificateId,
  accreditationType,
  expirationDate,
  startDate,
  rejectionReason,
  closeModal,
}: ButtonsBasedOnAccreditationStateProps) => {
  switch (accreditationState) {
    case 'pending': {
      return (
        <>
          <RejectAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            accreditationType={accreditationType}
            rejectionReason={rejectionReason}
            closeModal={closeModal}
          />
          <ApproveAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            accreditationType={accreditationType}
            expirationDate={expirationDate}
            closeModal={closeModal}
          />
        </>
      );
    }
    case 'rejected': {
      return (
        <>
          <ApproveAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            accreditationType={accreditationType}
            expirationDate={expirationDate}
            closeModal={closeModal}
          />
          <UpdateAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            expirationDate={expirationDate}
            startDate={startDate}
            accreditationType={accreditationType}
            accreditationState={accreditationState}
            rejectionReason={rejectionReason}
            closeModal={closeModal}
          />
        </>
      );
    }
    case 'verified': {
      return (
        <>
          <RejectAccreditationButton
            flexWorkerId={flexWorkerId}
            accreditationType={accreditationType}
            certificateId={certificateId}
            rejectionReason={rejectionReason}
            closeModal={closeModal}
          />
          <UpdateAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            expirationDate={expirationDate}
            startDate={startDate}
            accreditationType={accreditationType}
            accreditationState={accreditationState}
            rejectionReason={rejectionReason}
            closeModal={closeModal}
          />
        </>
      );
    }
    case 'all': {
      return (
        <>
          <RejectAccreditationButton
            flexWorkerId={flexWorkerId}
            accreditationType={accreditationType}
            certificateId={certificateId}
            rejectionReason={rejectionReason}
            closeModal={closeModal}
          />
          <UpdateAccreditationButton
            flexWorkerId={flexWorkerId}
            certificateId={certificateId}
            expirationDate={expirationDate}
            startDate={startDate}
            accreditationType={accreditationType}
            accreditationState={accreditationState}
            rejectionReason={rejectionReason}
            closeModal={closeModal}
          />
        </>
      );
    }
  }
};

const UpdateAccreditationButton = (props: UpdateAccreditationButtonProps) => {
  const { handleUpdateAccreditation } = useAccreditationDetailUpdateAction(props);

  return (
    <LwButton
      onClick={handleUpdateAccreditation}
      iconColorMode="stroke"
      color="primary"
      startIcon={<IconEdit />}
    >
      Bewerking
    </LwButton>
  );
};

const ApproveAccreditationButton = (props: ApproveAccreditationButtonProps) => {
  const { handleApproveAccreditation } = useAccreditationDetailApproveAction(props);

  return (
    <LwButton
      onClick={handleApproveAccreditation}
      iconColorMode="stroke"
      color="primary"
      startIcon={<IconApproved />}
    >
      Goedkeuren
    </LwButton>
  );
};

const RejectAccreditationButton = (props: RejectAccreditationButtonProps) => {
  const { handleRejectAccreditation, rejectButtonLabel } =
    useAccreditationDetailRejectAction(props);

  return (
    <LwButton
      onClick={handleRejectAccreditation}
      iconColorMode="stroke"
      color="secondary"
      startIcon={<IconCancel />}
    >
      {rejectButtonLabel}
    </LwButton>
  );
};
